<template>
  <div :class="props.containerClass">
    <label :class="[labelClass, 'mb-2', 'block', 'text-sm', 'font-semibold']">
      {{ props.title }}
    </label>
    <Multiselect
      v-model="selected"
      track-by="value"
      placeholder=""
      :label="label"
      :disabled="isDisabled"
      :allow-empty="false"
      :can-clear="false"
      :clear-on-select="false"
      :close-on-select="true"
      :native="true"
      :options="props.options"
      :searchable="searchable"
      :show-labels="false"
      :class="props.options.length === 1 ? 'adjustPadding' : ''"
      @update:model-value="changed"
      @open="emit('onMultiselectOpen')"
      @close="onMultiselectClose">
      <template #singleLabel="{ option }">
        <div class="flex items-center overflow-visible">
          <div
            v-if="option.iconKey"
            :class="[props.iconClass, 'flex-shrink-0']">
            <component :is="getOptionIcon(option.iconKey)" />
          </div>
          <div
            v-else-if="props.icon"
            :class="[props.iconClass, 'flex-shrink-0']">
            <component :is="props.icon" />
          </div>
          <span class="font-primary mr-auto">{{ option.label }}</span>
          <span>{{ option.rightSideLabel }}</span>
        </div>
      </template>
      <template #option="{ option }">
        <div class="flex items-center justify-between">
          <div
            class="flex flex-1 items-center"
            :class="
              'iconKey' in option ? '' : option.rightSideLabel ? 'ml-9' : ''
            ">
            <div
              v-if="'iconKey' in option"
              class="mr-3 h-6 w-6 flex-shrink-0 text-amenities-icon">
              <component
                v-if="option.iconKey"
                :is="getOptionIcon(option.iconKey)" />
            </div>
            <span class="flex-1">{{ option.label }}</span>
          </div>
          <span
            v-if="option.rightSideLabel"
            class="flex-shrink-0"
            :class="'iconKey' in option ? 'ml-4' : 'ml-auto mr-4'">
            {{ option.rightSideLabel }}
          </span>
        </div>
      </template>
    </Multiselect>
  </div>
</template>
<script setup>
import { onMounted, ref, watch, defineAsyncComponent } from 'vue';
import Multiselect from 'vue-multiselect';
const props = defineProps({
  title: String,
  options: Array,
  containerClass: {
    type: [String, Array],
    default: '',
  },
  multiSelectContainerClass: {
    type: String,
    default: '',
  },
  multiSelectWrapperClass: {
    type: String,
    default: '',
  },
  mode: {
    type: String,
    default: 'Single',
  },
  selectedObject: Object,
  placeholder: String,
  loadingLabel: String,
  icon: Object,
  iconClass: String,
  labelClass: String,
  optionClass: String,
  isDisabled: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
  },
  searchable: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits([
  'changedOption',
  'onMultiselectOpen',
  'onMultiselectClose',
]);

const getOptionIcon = (iconKey) => {
  if (!iconKey) return null;
  return defineAsyncComponent(() => import(`@/assets/icons/${iconKey}.svg`));
};

const onMultiselectClose = () => {
  emit('onMultiselectClose');
};

const selected = ref('');
onMounted(() => {
  selected.value = props.options[0];

  if (props.selectedObject && Object.keys(props.selectedObject).length) {
    selected.value = props.selectedObject;
    emit('changedOption', props.selectedObject);
  }
});

const changed = (value) => {
  emit('changedOption', value);
};

watch(
  () => props.selectedObject,
  (newVal) => {
    if (Object.keys(newVal).length === 0) {
      return;
    }
    selected.value = newVal;
  },
  { deep: true },
);
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.adjustPadding::v-deep(.multiselect__tags) {
  padding: 8px 16px 0 8px;
}
::v-deep(.multiselect__tags) {
  @apply border-border;
}
::v-deep(.multiselect__single) {
  @apply mb-0;
}
::v-deep(.multiselect__single span),
::v-deep(.multiselect__option span) {
  @apply text-xs leading-6 text-amenities-icon;
}
::v-deep(.multiselect__option--selected),
::v-deep(.multiselect__option--highlight) {
  @apply text-sm text-dark;
  background: #efefef;
}
::v-deep(.multiselect__option) {
  @apply border-0;
}
::v-deep(.multiselect__element) {
  @apply border-t border-border;
}
::v-deep(.multiselect__element:first-child) {
  @apply border-0;
}

.warning :deep(.multiselect__tags) {
  @apply border-error;
}

.warning :deep(.multiselect__single span) {
  @apply text-error;
}

.warning :deep(.multiselect__select::before) {
  @apply border-x-transparent border-b-error border-t-error;
}

:deep(.multiselect--disabled) {
  @apply pointer-events-auto cursor-not-allowed opacity-100;
}

:deep(.multiselect--disabled .multiselect__select) {
  @apply hidden;
}
:deep(.multiselect__content-wrapper) {
  width: auto !important;
  min-width: 100%;
  max-width: none;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}

:deep(.multiselect__content) {
  width: auto !important;
  min-width: 100%;
  max-width: none;
}

@media (min-width: 769px) {
  :deep(.multiselect__option) {
    white-space: nowrap;
    min-width: max-content;
  }
}

@media (max-width: 768px) {
  :deep(.multiselect__option span.flex-1) {
    white-space: normal;
    word-wrap: break-word;
    line-height: 1.3;
  }
}

:deep(.multiselect__single .flex > span:last-child) {
  text-align: right;
}
</style>
